import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Newspaper, ExternalLink, RefreshCw, AlertCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomsNewsSection() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/customs-news`);
      if (!response.ok) throw new Error("Failed to fetch news");
      const data = await response.json();
      setNews(data);
    } catch (err) {
      setError("Не удалось загрузить новости");
      console.error("Error fetching customs news:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  return (
    <section 
      id="customs-news" 
      className="py-24 bg-[#0A0A0A]"
      data-testid="customs-news-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 mb-12"
        >
          <div>
            <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-4">
              ГТК Беларуси
            </p>
            <h2 className="text-4xl md:text-5xl text-white font-normal">
              Новости таможни
            </h2>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={fetchNews}
              disabled={loading}
              className="text-white/40 hover:text-white text-xs uppercase tracking-widest transition-colors duration-300 flex items-center gap-2 disabled:opacity-50"
              data-testid="refresh-news-btn"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5} />
              Обновить
            </button>
            <a
              href="https://t.me/customs_bel"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-premium-outline text-xs py-3 px-6"
              data-testid="telegram-channel-link"
            >
              @customs_bel
            </a>
          </div>
        </motion.div>

        {/* News Content */}
        {loading && news.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <RefreshCw className="w-6 h-6 text-white/30 animate-spin" strokeWidth={1} />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center gap-3 py-24 text-white/40">
            <AlertCircle className="w-4 h-4" strokeWidth={1} />
            <span className="text-sm">{error}</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {news.map((post, index) => (
              <motion.a
                key={post.id}
                href={post.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="group bg-[#0A0A0A] p-8 hover:bg-[#0F0F0F] transition-colors duration-500"
                data-testid={`news-item-${index}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/30 text-xs">
                    {post.date}
                  </span>
                  {post.has_image && (
                    <span className="text-white/20 text-xs uppercase tracking-wider">
                      Фото
                    </span>
                  )}
                </div>
                
                <p className="text-white/70 text-sm leading-relaxed mb-6 line-clamp-4 group-hover:text-white/90 transition-colors duration-500">
                  {post.text}
                </p>
                
                <div className="flex items-center gap-2 text-white/30 group-hover:text-white/60 transition-colors duration-500">
                  <span className="text-xs uppercase tracking-wider">Читать</span>
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </div>
              </motion.a>
            ))}
          </div>
        )}

        {/* Footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mt-8 text-center text-white/20 text-xs uppercase tracking-wider"
        >
          Официальный канал Государственного таможенного комитета
        </motion.p>
      </div>
    </section>
  );
}
